import { getTranslationNamespaces } from '@frontend/AppSettings/environmentConfig';
import {
  DEFAULT_LOCALE,
  localeNamespaces,
} from '@frontend/Dependencies/i18n/SupportedLanguages';
import { I18nService } from '@frontend/Models/I18n/I18nModels';
import { I18n } from 'i18n-js';
import { LocalizationService } from './LocalizationService';

const resolveInitialLocale = (): string => {
  const detectedLocale = LocalizationService.getLocales()?.[0]?.languageCode;
  if (!detectedLocale) {
    return DEFAULT_LOCALE;
  }

  const [languageCode] = detectedLocale.split(/[-_]/);
  return languageCode || DEFAULT_LOCALE;
};

const buildTranslations = (): Record<string, unknown> => {
  const namespacesForLocale = (localeNamespaces[DEFAULT_LOCALE] ?? {}) as Record<string, Record<string, unknown>>;
  const requestedNamespaces = getTranslationNamespaces();
  const orderedNamespaces = [
    'common',
    ...requestedNamespaces.filter((namespace) => namespace !== 'common'),
  ];

  return orderedNamespaces.reduce<Record<string, unknown>>((acc, namespace) => {
    const resource = namespacesForLocale[namespace];

    if (!resource) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          `Missing translation namespace "${namespace}" for locale ${DEFAULT_LOCALE}.`,
        );
      }
      return acc;
    }

    return {
      ...acc,
      ...resource,
    };
  }, {});
};

const i18n = new I18n(
  { [DEFAULT_LOCALE]: buildTranslations() },
  {
    enableFallback: true,
    locale: resolveInitialLocale(),
    defaultLocale: DEFAULT_LOCALE,
  },
);

/**
 * This service is used to translate the application.
 */
export const i18nService: I18nService = {
  /**
   * Translates the given scope.
   *
   * @param scope The scope to translate.
   * @param options Options to pass to the translation.
   */
  t: (scope, options) => i18n.t(scope, options),
  /**
   * Get all available locales.
   */
  getAvailableLocales: () => i18n.locales,
  /**
   * Get the current locale.
   */
  getLocale: () => i18n.locale,
  /**
   * Change the current locale.
   *
   * @param locale - The locale to change to.
   */
  changeLanguage: (locale) => (i18n.locale = locale),
  /**
   * Format value as currency.
   *
   * @param value - The value to format.
   * @param fractionDigits - The number of fraction digits to use.
   */
  currency: (value, minimumFractionDigits = 2) => {
    return new Intl.NumberFormat(i18n.locale.replace('_', '-'), {
      minimumFractionDigits,
      style: 'currency',
      currency: 'CAD',
      currencySign: 'standard',
      currencyDisplay: 'symbol',
    })
      .format(value)
      .replace('CA$', '$'); // Removing the country code from the currency
  },
};
